/**
 * Faculty dashboard — profile, odd/even term, allocated subjects, syllabus, anonymous preview
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import RoleDashboardShell from '../components/RoleDashboardShell';
import {
  facultyMenuItems,
  facultyMenuGroups,
  isValidFacultyMenuId,
} from '../config/roleDashboardMenus';
import { AcademicCapIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'upcoming', label: 'Upcoming' },
];

const CRITERION_LABELS = {
  TEACHING_QUALITY: 'Teaching quality',
  SUBJECT_KNOWLEDGE: 'Subject knowledge',
  COMMUNICATION: 'Communication',
  PUNCTUALITY: 'Punctuality',
  DOUBT_RESOLUTION: 'Doubt resolution',
  COURSE_COVERAGE: 'Course coverage',
  ASSESSMENT_FAIRNESS: 'Assessment fairness',
};

function openFacultyWindow(path) {
  const base = process.env.PUBLIC_URL || '';
  const url = `${window.location.origin}${base}${path}`;
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
    return;
  }
  window.location.assign(url);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name) {
  return (name || 'FC')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function Field({ label, value }) {
  return (
    <div className="profile-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

export default function FacultyDashboard() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileForm, setProfileForm] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [syllabusSubject, setSyllabusSubject] = useState(null);
  const [syllabusTitle, setSyllabusTitle] = useState('Course syllabus');
  const [syllabusNotes, setSyllabusNotes] = useState('');
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [syllabusBusy, setSyllabusBusy] = useState(false);
  const [reports, setReports] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const fetchWorkspace = useCallback(async () => {
    try {
      const [boardRes, reportsRes, statsRes] = await Promise.all([
        api.get('/faculty/dashboard'),
        api.get('/faculty/reports').catch(() => ({ data: { data: null } })),
        api.get('/faculty/statistics').catch(() => ({ data: { data: null } })),
      ]);
      const data = boardRes.data?.data || null;
      setWorkspace(data);
      setReports(reportsRes.data?.data || null);
      setStatistics(statsRes.data?.data || null);
      if (data?.profile) {
        setProfileForm({
          contactNumber: data.profile.contactNumber || '',
          officeLocation: data.profile.officeLocation || '',
          qualification: data.profile.qualification || '',
          highestQualification: data.profile.highestQualification || '',
          specialization: data.profile.specialization || '',
          researchArea: data.profile.researchArea || '',
          profileSummary: data.profile.profileSummary || '',
        });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load faculty dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspace();
    const onFocus = () => fetchWorkspace();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchWorkspace]);

  const profile = workspace?.profile;
  const term = workspace?.term;
  const cycle = workspace?.cycle;
  const schedule = workspace?.schedule || [];
  const stats = workspace?.stats || { active: 0, pending: 0, completed: 0, upcoming: 0 };
  const displayName = profile?.facultyName
    || (profile?.firstName ? `${profile.title ? `${profile.title} ` : ''}${profile.firstName} ${profile.lastName || ''}`.trim() : null)
    || (user?.faculty ? `${user.faculty.first_name} ${user.faculty.last_name}` : user?.email);
  const termLabel = term?.term_type === 'EVEN' ? 'Even' : term?.term_type === 'ODD' ? 'Odd' : null;

  const filteredSubjects = useMemo(() => {
    const list = workspace?.subjects || [];
    return statusFilter === 'all' ? list : list.filter((subject) => subject.status === statusFilter);
  }, [workspace, statusFilter]);

  const handlePreview = (subject) => {
    if (!subject.canPreview) return;
    const params = new URLSearchParams({
      subjectId: subject.id,
      cycleId: subject.cycleId || '',
      subjectName: subject.name || '',
      subjectCode: subject.code || '',
    });
    openFacultyWindow(`/dashboard/faculty/feedback?${params.toString()}`);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage('');
    setError('');
    try {
      const res = await api.patch('/faculty/profile', profileForm);
      setWorkspace(res.data?.data || workspace);
      setProfileMessage('Your profile details were saved.');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveSyllabus = async (e) => {
    e.preventDefault();
    if (!syllabusSubject) return;
    setSyllabusBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('title', syllabusTitle);
      form.append('notes', syllabusNotes);
      form.append('academicYear', syllabusSubject.academicYear || '');
      form.append('semester', String(syllabusSubject.semester || ''));
      if (syllabusFile) form.append('file', syllabusFile);
      await api.post(`/faculty/subjects/${syllabusSubject.id}/syllabus`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSyllabusSubject(null);
      setSyllabusFile(null);
      await fetchWorkspace();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not save syllabus');
    } finally {
      setSyllabusBusy(false);
    }
  };

  const handleDeleteSyllabus = async (subject) => {
    if (!window.confirm(`Remove the syllabus for ${subject.code}?`)) return;
    setSyllabusBusy(true);
    setError('');
    try {
      await api.delete(`/faculty/subjects/${subject.id}/syllabus`, {
        params: { academicYear: subject.academicYear, semester: subject.semester },
      });
      await fetchWorkspace();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not delete syllabus');
    } finally {
      setSyllabusBusy(false);
    }
  };

  const handlePreviewSyllabus = async (subject) => {
    try {
      const res = await api.get(`/faculty/subjects/${subject.id}/syllabus/file`, {
        params: { academicYear: subject.academicYear, semester: subject.semester },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'No syllabus file to preview. Upload a PDF or document first.');
    }
  };

  const downloadPdf = async (cycleId, label) => {
    setError('');
    setPdfDownloading(true);
    try {
      const res = await api.get(`/faculty/reports/${cycleId}/pdf`, { responseType: 'blob' });
      const blob = res.data.type ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `faculty-performance-${(label || cycleId).toString().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  const summary = statistics?.summary || {};
  const performance = Array.isArray(reports?.performance) ? reports.performance : [];
  const cycleIds = [...new Set(performance.map((row) => row.cycle_id).filter(Boolean))];
  const cycleLabels = {};
  performance.forEach((row) => {
    if (row.cycle_id) cycleLabels[row.cycle_id] = row.cycle_name || row.academic_year || row.cycle_id;
  });

  return (
    <RoleDashboardShell
      title="Faculty Dashboard"
      subtitle={`${displayName}${profile?.departmentName || profile?.department ? ` · ${profile.departmentName || profile.department}` : ''}${profile?.designation ? ` · ${profile.designation}` : ''}`}
      brandTitle="Faculty Console"
      brandSubtitle={term ? `${term.term_type} term · ${term.academic_year}` : 'No active term'}
      menuItems={facultyMenuItems}
      menuGroups={facultyMenuGroups}
      isValidMenuId={isValidFacultyMenuId}
      loading={loading}
      error={error}
    >
      {({ activeMenu }) => {
        if (activeMenu === 'overview') {
          return (
            <div className="space-y-6">
              <div className="fac-hero">
                <div className="stu-hero-row">
                  <div className="stu-hero-main">
                    <span className="stu-avatar">{initials(displayName)}</span>
                    <div>
                      <p className="text-sm text-white/80 mb-1">Teaching desk</p>
                      <h2 className="text-2xl font-semibold m-0">{displayName}</h2>
                      <p className="mt-1 text-white/85 text-sm">
                        {[profile?.facultyCode || profile?.employeeId, profile?.designation, profile?.departmentName || profile?.department]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="stu-hero-cycle">
                    <p className="text-xs uppercase tracking-wide text-white/70 m-0">Active semester window</p>
                    <p className="font-semibold m-0 mt-1">
                      {termLabel ? `${termLabel} semester` : 'Term not published'}
                      {term?.academic_year ? ` · ${term.academic_year}` : ''}
                    </p>
                    <p className="text-sm text-white/80 m-0 mt-1">{cycle?.name || 'Student feedback cycle not opened yet'}</p>
                    {cycle ? (
                      <p className="text-xs text-white/70 m-0 mt-1">
                        {formatDate(cycle.start_date)} – {formatDate(cycle.end_date)}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="stu-stat-grid">
                {[
                  { key: 'active', label: 'Active' },
                  { key: 'pending', label: 'Pending' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'upcoming', label: 'Upcoming' },
                ].map((item) => (
                  <button key={item.key} type="button" className={`stu-stat stu-stat-${item.key} text-left`} onClick={() => setStatusFilter(item.key)}>
                    <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                    <p className="stu-stat-value">{stats[item.key] || 0}</p>
                  </button>
                ))}
              </div>

              <div className="stu-filter-bar">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setStatusFilter(filter.id)}
                    className={`stu-filter ${statusFilter === filter.id ? 'is-on' : ''}`}
                  >
                    {filter.label}
                    {filter.id !== 'all' ? ` · ${stats[filter.id] || 0}` : ` · ${workspace?.subjects?.length || 0}`}
                  </button>
                ))}
              </div>

              {filteredSubjects.length === 0 ? (
                <div className="admin-empty">
                  <p className="admin-empty-title">No allocated subjects</p>
                  <p className="admin-empty-text">Subjects assigned by admin appear here with enrolment and feedback progress.</p>
                </div>
              ) : (
                <div className="stu-subject-grid">
                  {filteredSubjects.map((subject) => {
                    const percent = subject.enrolled > 0 ? Math.min(100, Math.round((subject.submitted / subject.enrolled) * 100)) : 0;
                    return (
                      <article key={subject.allocationId || subject.id} className={`stu-subject-card is-${subject.status}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{subject.code}</p>
                            <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{subject.name}</h3>
                          </div>
                          <span className={`stu-chip stu-chip-${subject.status}`}>{subject.status}</span>
                        </div>
                        <dl className="grid grid-cols-2 gap-2 text-sm m-0">
                          <div><dt className="text-xs" style={{ color: 'var(--text-muted)' }}>Program</dt><dd className="m-0 font-medium">{subject.program}</dd></div>
                          <div><dt className="text-xs" style={{ color: 'var(--text-muted)' }}>Semester</dt><dd className="m-0 font-medium">{subject.semester} · {subject.academicYear}</dd></div>
                          <div><dt className="text-xs" style={{ color: 'var(--text-muted)' }}>Division</dt><dd className="m-0 font-medium">{subject.division}</dd></div>
                          <div><dt className="text-xs" style={{ color: 'var(--text-muted)' }}>Average</dt><dd className="m-0 font-medium">{subject.averageRating != null ? Number(subject.averageRating).toFixed(2) : '—'}</dd></div>
                        </dl>
                        <div>
                          <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                            <span>Feedback given</span>
                            <span>{subject.submitted} / {subject.enrolled} students</span>
                          </div>
                          <div className="fac-meter"><span style={{ width: `${percent}%` }} /></div>
                          {subject.status === 'pending' ? (
                            <p className="text-xs mt-1 m-0" style={{ color: 'var(--text-muted)' }}>
                              Pending · {subject.submitted} of {subject.enrolled} students have submitted
                            </p>
                          ) : null}
                          {!subject.syllabusSubmitted ? (
                            <p className="text-xs mt-1 m-0 text-amber-700">Submit the course syllabus before the feedback cycle opens.</p>
                          ) : (
                            <p className="text-xs mt-1 m-0" style={{ color: 'var(--text-muted)' }}>Syllabus on file{subject.syllabusFileName ? ` · ${subject.syllabusFileName}` : ''}</p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-auto">
                          <button type="button" className="btn-secondary" disabled={!subject.canPreview} onClick={() => handlePreview(subject)}>
                            Preview feedback
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => {
                              setSyllabusSubject(subject);
                              setSyllabusTitle(subject.syllabusTitle || `${subject.code} syllabus`);
                              setSyllabusNotes('');
                              setSyllabusFile(null);
                            }}
                          >
                            {subject.syllabusSubmitted ? 'Update syllabus' : 'Submit syllabus'}
                          </button>
                          {subject.syllabusSubmitted ? (
                            <>
                              <button type="button" className="btn-secondary" onClick={() => handlePreviewSyllabus(subject)}>Preview syllabus</button>
                              <button type="button" className="btn-secondary" disabled={syllabusBusy} onClick={() => handleDeleteSyllabus(subject)}>Delete syllabus</button>
                            </>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {syllabusSubject ? (
                <div className="admin-card">
                  <div className="admin-card-body">
                    <div className="flex items-center gap-3 mb-4">
                      <DocumentArrowUpIcon className="h-6 w-6 text-indigo-600" />
                      <div>
                        <h2 className="admin-section-title">Course syllabus · {syllabusSubject.code}</h2>
                        <p className="admin-section-subtitle mb-0">Required at the start of the semester, before student feedback begins. Allocation details stay read-only.</p>
                      </div>
                    </div>
                    <form onSubmit={handleSaveSyllabus} className="space-y-4">
                      <div>
                        <label className="input-label">Title</label>
                        <input className="input" value={syllabusTitle} onChange={(e) => setSyllabusTitle(e.target.value)} maxLength={200} />
                      </div>
                      <div>
                        <label className="input-label">Syllabus notes</label>
                        <textarea className="input min-h-[120px]" value={syllabusNotes} onChange={(e) => setSyllabusNotes(e.target.value)} placeholder="Unit-wise outline, outcomes, textbooks" />
                      </div>
                      <div>
                        <label className="input-label">Upload file (PDF, Word, or text)</label>
                        <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => setSyllabusFile(e.target.files?.[0] || null)} />
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="btn-primary" disabled={syllabusBusy}>{syllabusBusy ? 'Saving…' : 'Save syllabus'}</button>
                        <button type="button" className="btn-secondary" onClick={() => setSyllabusSubject(null)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                </div>
              ) : null}
            </div>
          );
        }

        if (activeMenu === 'profile') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="stu-identity">
                  <span className="stu-avatar-sm" style={{ width: '3.25rem', height: '3.25rem', fontSize: '1.1rem' }}>{initials(displayName)}</span>
                  <div>
                    <h2 className="admin-section-title mb-1">Faculty profile</h2>
                    <p className="admin-section-subtitle mb-0">Master identity is read-only. You may update contact, office, and qualification notes.</p>
                  </div>
                </div>
                {!profile ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">Profile not linked</p>
                    <p className="admin-empty-text">Ask admin to attach a faculty master record to this login.</p>
                  </div>
                ) : (
                  <>
                    <dl className="profile-grid mb-8">
                      <Field label="Faculty name" value={displayName} />
                      <Field label="Faculty code" value={profile.facultyCode || profile.employeeId} />
                      <Field label="Designation" value={profile.designation} />
                      <Field label="Department" value={profile.departmentName || profile.department} />
                      <Field label="Employment" value={profile.employmentType} />
                      <Field label="Status" value={profile.facultyStatus} />
                      <Field label="Experience (years)" value={profile.experienceYears} />
                      <Field label="Date of joining" value={profile.dateOfJoining ? new Date(profile.dateOfJoining).toLocaleDateString() : ''} />
                      <Field label="Email" value={profile.email || user?.email} />
                    </dl>
                    {profileForm ? (
                      <form onSubmit={handleSaveProfile} className="space-y-4">
                        <h3 className="admin-section-title">Editable details</h3>
                        {profileMessage ? <p className="text-sm text-emerald-700">{profileMessage}</p> : null}
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="input-label">Contact number</label>
                            <input className="input" value={profileForm.contactNumber} onChange={(e) => setProfileForm((f) => ({ ...f, contactNumber: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Office location</label>
                            <input className="input" value={profileForm.officeLocation} onChange={(e) => setProfileForm((f) => ({ ...f, officeLocation: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Qualification</label>
                            <input className="input" value={profileForm.qualification} onChange={(e) => setProfileForm((f) => ({ ...f, qualification: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Highest qualification</label>
                            <input className="input" value={profileForm.highestQualification} onChange={(e) => setProfileForm((f) => ({ ...f, highestQualification: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Specialization</label>
                            <input className="input" value={profileForm.specialization} onChange={(e) => setProfileForm((f) => ({ ...f, specialization: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Research area</label>
                            <textarea className="input min-h-[80px]" value={profileForm.researchArea} onChange={(e) => setProfileForm((f) => ({ ...f, researchArea: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Profile summary</label>
                            <textarea className="input min-h-[100px]" value={profileForm.profileSummary} onChange={(e) => setProfileForm((f) => ({ ...f, profileSummary: e.target.value }))} />
                          </div>
                        </div>
                        <button type="submit" className="btn-primary" disabled={profileSaving}>{profileSaving ? 'Saving…' : 'Save my profile'}</button>
                      </form>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        }

        if (activeMenu === 'academics') {
          return (
            <div className="space-y-6">
              <div className="fac-hero">
                <p className="text-sm text-white/80 m-0">Department · Program · Semester</p>
                <h2 className="text-2xl font-semibold m-0 mt-1">{profile?.departmentName || profile?.department || 'Department'}</h2>
                <p className="mt-2 text-white/85 text-sm m-0">
                  {[profile?.designation, termLabel ? `${termLabel} term` : null, term?.academic_year]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Academic identity</h2>
                  <dl className="profile-grid">
                    <Field label="Institute" value={user?.institute?.name} />
                    <Field label="Department" value={profile?.departmentName || profile?.department} />
                    <Field label="Department code" value={profile?.departmentCode} />
                    <Field label="Designation" value={profile?.designation} />
                    <Field label="Faculty code" value={profile?.facultyCode || profile?.employeeId} />
                    <Field label="Academic year" value={term?.academic_year} />
                  </dl>
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h2 className="admin-section-title mb-0">Active semester window</h2>
                    {term ? (
                      <span className={`stu-chip ${term.term_type === 'EVEN' ? 'stu-chip-even' : 'stu-chip-odd'}`}>
                        {term.term_type} semester
                      </span>
                    ) : null}
                  </div>
                  {term ? (
                    <dl className="profile-grid">
                      <Field label="Academic year" value={term.academic_year} />
                      <Field label="Term" value={term.term_type === 'EVEN' ? 'Even (2, 4, 6, 8)' : 'Odd (1, 3, 5, 7)'} />
                      <Field label="Semesters in this window" value={(term.semester_numbers || []).join(', ')} />
                      <Field label="Starts" value={formatDate(term.start_at)} />
                      <Field label="Ends" value={formatDate(term.end_at)} />
                      <Field label="Feedback cycle" value={cycle?.name || 'Not opened yet'} />
                    </dl>
                  ) : (
                    <p style={{ color: 'var(--text-muted)' }}>Admin has not published an odd/even academic term calendar yet.</p>
                  )}
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Step-by-step feedback schedule</h2>
                  <p className="admin-section-subtitle">Faculty and student stages for this institute. Live teaching-feedback stages stay highlighted.</p>
                  {schedule.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)' }}>No lifecycle stages configured yet.</p>
                  ) : (
                    <div className="stu-timeline">
                      {schedule.map((stage, index) => (
                        <div key={stage.id} className={`stu-timeline-item ${stage.isCurrent ? 'current' : ''}`}>
                          <span className={`stu-chip ${stage.isCurrent ? 'stu-chip-active' : 'stu-chip-upcoming'}`}>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div>
                            <p className="font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{stage.name}</p>
                            <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
                              {stage.stakeholder} · {stage.applies_to?.replace(/_/g, ' ')}
                              {stage.isCurrent ? ' · Live now' : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (activeMenu === 'reports') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <h2 className="admin-section-title">My Feedback Summary</h2>
                <p className="admin-section-subtitle">Read-only performance view. Download a PDF of approved data.</p>
                {performance.length > 0 ? (
                  <>
                    <div className="admin-master-table-wrap mb-4">
                      <table className="admin-master-table">
                        <thead>
                          <tr>
                            <th>Subject</th>
                            <th>Cycle / Year</th>
                            <th>Feedbacks</th>
                            <th>Avg rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {performance.map((row, i) => (
                            <tr key={row.subject_id || i}>
                              <td>{row.subject_code || row.subject_name || '—'}</td>
                              <td>{row.cycle_name || row.academic_year || '—'}</td>
                              <td>{row.total_feedbacks ?? '—'}</td>
                              <td>{row.avg_overall_rating != null ? Number(row.avg_overall_rating).toFixed(2) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex flex-wrap gap-3 items-center">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Download report:</span>
                      {cycleIds.length > 0 ? cycleIds.map((cid) => (
                        <button key={cid} type="button" disabled={pdfDownloading} onClick={() => downloadPdf(cid, cycleLabels[cid])} className="btn-primary">
                          {pdfDownloading ? 'Downloading…' : `PDF: ${cycleLabels[cid] || cid.slice(0, 8)}`}
                        </button>
                      )) : <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No approved cycle to download.</span>}
                    </div>
                  </>
                ) : (
                  <div className="admin-empty">
                    <p className="admin-empty-title">No performance records yet</p>
                    <p className="admin-empty-text">Approved student feedback will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          );
        }

        if (activeMenu === 'statistics') {
          const breakdown = Array.isArray(statistics?.criterionBreakdown) ? statistics.criterionBreakdown : [];
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                    <AcademicCapIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="admin-section-title">Rating statistics</h2>
                    <p className="admin-section-subtitle mb-0">Approved feedback only, grouped by criterion.</p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3 mb-6">
                  <div className="stat-card">
                    <p className="stat-card-label">Overall average</p>
                    <p className="stat-card-value">{summary.overall_avg_rating != null ? Number(summary.overall_avg_rating).toFixed(2) : '—'}</p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-card-label">Lowest / highest</p>
                    <p className="stat-card-value">{summary.min_rating != null ? `${summary.min_rating} – ${summary.max_rating}` : '—'}</p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-card-label">Subjects taught</p>
                    <p className="stat-card-value">{summary.subjects_taught ?? '—'}</p>
                  </div>
                </div>
                {breakdown.length === 0 ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">No criterion data yet</p>
                    <p className="admin-empty-text">Statistics appear after admin approves student submissions.</p>
                  </div>
                ) : (
                  <div className="admin-master-table-wrap">
                    <table className="admin-master-table">
                      <thead>
                        <tr>
                          <th>Criterion</th>
                          <th>Responses</th>
                          <th>Average</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.map((row) => (
                          <tr key={row.criterion}>
                            <td>{CRITERION_LABELS[row.criterion] || row.criterion}</td>
                            <td>{row.count ?? '—'}</td>
                            <td>{row.avg_rating != null ? Number(row.avg_rating).toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        }

        return null;
      }}
    </RoleDashboardShell>
  );
}
